import { LeadPanelTabItem } from "./LeadPanelTabs";

export type ContactLeadPanelTab = "overview" | "research" | "timeline" | "profiles";

/** The subset of an organization the panel needs to build a clickable company link. */
interface CompanyUrlSource {
	websiteUrl?: string | null;
	primaryDomain?: string | null;
}

/**
 * The company website to link to from the lead panel's company card: the stored website URL, else
 * the primary domain. A bare domain (`example.com`) is upgraded to `https://` so it is a usable
 * `href`; a value that already carries a scheme is left alone.
 */
export const resolveCurrentCompanyUrl = (
	organization?: CompanyUrlSource | null
): string | undefined => {
	const url = organization?.websiteUrl || organization?.primaryDomain;

	if (!url) {
		return undefined;
	}

	return url.includes("http") ? url : `https://${url}`;
};

interface ContactLeadPanelTabsArgs {
	t: (key: string) => string;
	/** Research is feature-gated (DEEP_RESEARCH); its tab is omitted when inactive/unresolved. */
	isDeepResearchActive?: boolean;
	researchCount: number;
	profilesCount: number;
}

/** The panel's content tabs, in display order, with their count badges. */
export const buildContactLeadPanelTabs = ({
	t,
	isDeepResearchActive,
	researchCount,
	profilesCount,
}: ContactLeadPanelTabsArgs): LeadPanelTabItem<ContactLeadPanelTab>[] => [
	{ label: t("contacts.leadPanel.tabs.overview"), value: "overview" },
	...(isDeepResearchActive
		? [
				{
					label: t("contacts.leadPanel.tabs.research"),
					value: "research" as const,
					count: researchCount,
				},
			]
		: []),
	{ label: t("contacts.leadPanel.tabs.timeline"), value: "timeline" },
	{ label: t("contacts.leadPanel.tabs.profiles"), value: "profiles", count: profilesCount },
];
